import { DEFAULT_CALENDAR_COLOURS, surfaceMode } from '@mfw/common';

import type { CalendarColourMode } from '@mfw/common';

import { applyScheduleMarks, stampCalendarMode } from './scheduleMarks';
import { applyOnSurfaceRamps } from './onSurface';
import { applyBrandCta } from './brandCta';

/**
 * Writes a RESOLVED theme record onto an element: every colour in it, the
 * player's own colour, the surface flag, the calendar colour mode, and the
 * aliases measured from them (`onSurface` inks and marks, the calendar's
 * schedule tones, the gradient CTA).
 *
 * The one writer behind both paths that paint `:root` from a whole record —
 * the provider applying the saved theme (`applyThemeToRoot`) and the rebrand
 * editor previewing a draft (`applyResolvedThemePreview`). One writer, so the
 * preview cannot promise a palette, a flag or a measured ink the save would
 * then paint differently.
 *
 * Colours, plus the one setting colours are measured FROM: the calendar mode
 * (`calendarColours`, Brand when the caller has none — an org that never chose
 * and every org-less context alike). It is stamped as `data-mfw-calendar`
 * before anything is measured, because the schedule tones read it. The other
 * settings stay with their owners: the provider stamps the glow itself, and a
 * draft preview does not own the session's glow at all.
 *
 * The flag is the half CSS cannot compute for itself. A stylesheet can read a
 * var but not ask which END of the ladder it is on, so a semantic ramp had no
 * way to pick the step that contrasts with the surface — `success-light`, the
 * right choice on the dark ladder, reads at 1.22:1 on a cream one. `data-mfw-
 * surface` is the CSS-side twin of the JS palette's `mode`, derived through the
 * same `surfaceMode` rule so the two cannot disagree about one background.
 */
export function writeResolvedTheme(
  root: HTMLElement,
  theme: Readonly<Record<string, string>>,
  calendarColours: CalendarColourMode = DEFAULT_CALENDAR_COLOURS,
) {
  stampCalendarMode(root, calendarColours);

  for (const variable in theme) {
    root.style.setProperty(variable, theme[variable]);
  }
  /**
   * Sets up custom plyr color for video player.
   */
  root.style.setProperty(
    '--plyr-color-main',
    theme['--mfw-palette-primary-main'],
  );
  root.dataset.mfwSurface = surfaceMode(
    theme['--mfw-palette-background-default'],
  );

  const readThemeValue = (varName: string) => theme[varName];
  applyOnSurfaceRamps(root, readThemeValue);
  applyScheduleMarks(root, readThemeValue);
  // Measured, not derived: no transform walk reaches the CTA fill, and it is
  // the most conspicuous thing on the page being previewed.
  applyBrandCta(root, readThemeValue);
}
